/**
 * The practice yard's overlays: coach card, weapon bar, objective cue, gear
 * window and the map guide.
 *
 * One slice of the base-view stylesheet; see styles/index.ts. Worn-metal look
 * from the Higgsfield clutter kit in /public/clutter/ (see app.css, "Wear and
 * clutter") — the rule is that every panel carries some of it.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it.
 */
export default `
.bv-tm-coach, .bv-tm-gear, .bv-tm-card, .bv-tm-slot, .bv-tm-toast {
  font: 13px/1.45 var(--font-technical); color: var(--bone);
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  background:
    linear-gradient(rgba(14,9,5,0.82), rgba(14,9,5,0.9)),
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  box-shadow: 0 0 0 1px #120d08, 0 10px 26px rgba(0,0,0,0.6), inset 0 0 22px rgba(0,0,0,0.5);
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}

/* ── Coach card ─────────────────────────────────────────────────────────── */
.bv-tm-coach {
  position: absolute; top: 14px; left: 14px; z-index: 6;
  width: min(400px, calc(100% - 28px)); padding: 12px 16px 12px;
  isolation: isolate;
}
.bv-tm-coach::before {
  content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
  background:
    repeating-linear-gradient(135deg, #d9a020 0 10px, #17110a 10px 20px) 0 0 / 100% 4px no-repeat,
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 20% 30% / 420px 420px;
  opacity: 0.8;
}
.bv-tm-coach::after {
  content: ''; position: absolute; top: -22px; right: -26px; width: 84px; height: 84px; pointer-events: none;
  background: url('/clutter/clutter-a.webp') center / contain no-repeat;
  transform: rotate(10deg); filter: drop-shadow(0 3px 4px rgba(0,0,0,0.6));
}
.bv-tm-kicker { color: var(--brass-bright); font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; }
.bv-tm-title { margin: 2px 0 4px; font: 800 20px var(--font-heading, var(--font-technical)); letter-spacing: 0.05em; text-transform: uppercase; color: var(--brass-hot); }
.bv-tm-goal { margin: 0 0 8px; font-size: 15px; color: var(--bone); }
.bv-tm-note { margin: 0 0 8px; color: var(--bone-dim); font-size: 11px; }
.bv-tm-tips { margin: 0 0 8px; padding-left: 18px; color: var(--bone-dim); font-size: 12px; }
.bv-tm-dots { display: flex; gap: 5px; margin-bottom: 8px; }
.bv-tm-dot { width: 22px; height: 4px; background: #150f08; border: 1px solid rgba(122,90,48,0.4); }
.bv-tm-dot-done { background: var(--moss); border-color: var(--moss); }
.bv-tm-dot-now { background: var(--brass-hot); border-color: var(--brass-hot); }
.bv-tm-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.bv-tm-count { color: var(--brass-hot); font-weight: 700; font-size: 16px; font-variant-numeric: tabular-nums; }
.bv-tm-link {
  background: none; border: 0; padding: 6px 2px; cursor: pointer; margin-left: auto;
  color: var(--bone-faint); font: 11px var(--font-technical); letter-spacing: 0.08em; text-transform: uppercase;
  border-bottom: 1px dotted currentColor;
}
.bv-tm-link:hover { color: var(--bone); }
.bv-tm-btn {
  min-height: 44px; padding: 0 18px; cursor: pointer; color: var(--brass-bright);
  font: 700 13px var(--font-technical); letter-spacing: 0.12em; text-transform: uppercase;
  background: linear-gradient(170deg, var(--panel-a), var(--well));
  border: 1px solid var(--brass); border-radius: var(--radius-well);
}
.bv-tm-btn:disabled { opacity: 0.4; cursor: default; }
.bv-tm-primary { color: #1a1208; background: linear-gradient(180deg, var(--brass-hot), #b8862e); border-color: var(--brass-hot); }
.bv-tm-coach .bv-tm-btn { margin-top: 10px; width: 100%; }

/* ── Weapon bar ─────────────────────────────────────────────────────────── */
.bv-tm-hud {
  position: absolute; left: 50%; bottom: max(18px, env(safe-area-inset-bottom)); transform: translateX(-50%);
  z-index: 6; display: flex; gap: 8px;
}
.bv-tm-slot {
  position: relative; min-width: 96px; min-height: 48px; padding: 6px 12px 6px 34px; cursor: pointer;
  display: flex; align-items: center; gap: 8px; text-align: left; color: var(--bone-dim);
}
.bv-tm-slot-on { border-color: var(--brass-hot); color: var(--brass-hot); box-shadow: 0 0 0 1px rgba(255,189,92,0.3), 0 10px 26px rgba(0,0,0,0.6); }
.bv-tm-slot-pulse { animation: bv-tm-pulse 1.1s ease-in-out infinite; }
.bv-tm-key { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); font-weight: 800; color: var(--brass-bright); }
.bv-tm-slotname { font-size: 12px; letter-spacing: 0.04em; }
.bv-tm-ammo { margin-left: auto; font-weight: 700; color: var(--brass-hot); font-variant-numeric: tabular-nums; }
@keyframes bv-tm-pulse { 50% { border-color: var(--brass-hot); box-shadow: 0 0 14px rgba(255,189,92,0.55); } }

/* ── Toast ──────────────────────────────────────────────────────────────── */
.bv-tm-toast { position: absolute; left: 50%; bottom: 88px; transform: translateX(-50%); z-index: 7; padding: 8px 16px; font-weight: 700; }
.bv-tm-toast[hidden] { display: none; }

/* ── Objective cue ──────────────────────────────────────────────────────── */
.bv-tm-guide {
  position: absolute; z-index: 5; pointer-events: none; width: 0; height: 0;
  border-left: 14px solid transparent; border-right: 14px solid transparent; border-top: 22px solid var(--brass-hot);
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.8)); animation: bv-tm-bob 0.9s ease-in-out infinite;
}
.bv-tm-guide[hidden] { display: none; }
.bv-tm-guide-edge { animation: none; }
@keyframes bv-tm-bob { 50% { margin-top: -8px; } }

/* ── Gear window ────────────────────────────────────────────────────────── */
.bv-tm-gear {
  position: absolute; top: 50%; right: 18px; transform: translateY(-50%); z-index: 8;
  width: min(280px, calc(100% - 36px)); padding: 14px 16px;
}
.bv-tm-gear[hidden] { display: none; }
.bv-tm-item { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 6px 0 10px; }
.bv-tm-itemname { font-weight: 700; }

/* ── Map guide ──────────────────────────────────────────────────────────── */
.bv-tm-modal { position: absolute; inset: 0; z-index: 9; display: grid; place-items: center; background: rgba(6,4,2,0.7); padding: 16px; }
.bv-tm-modal[hidden] { display: none; }
.bv-tm-card { width: min(460px, 100%); padding: 18px 20px 16px; }
.bv-tm-badge {
  display: inline-block; margin-bottom: 8px; padding: 4px 12px; font: 800 18px var(--font-technical); letter-spacing: 0.2em;
  color: #1a1208; background: repeating-linear-gradient(135deg, #d9a020 0 10px, #b8862e 10px 20px);
  border: 1px solid #120d08;
}
.bv-tm-lines { margin: 8px 0 16px; padding-left: 20px; font-size: 15px; line-height: 1.55; }
.bv-tm-lines li { margin-bottom: 4px; }
.bv-tm-card .bv-tm-row { justify-content: space-between; }

@media (max-width: 640px) {
  .bv-tm-coach { top: 8px; left: 8px; padding: 10px 12px; }
  .bv-tm-tips { display: none; }
  .bv-tm-hud { gap: 6px; }
  .bv-tm-slot { min-width: 0; padding-right: 8px; }
  .bv-tm-slotname { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .bv-tm-guide, .bv-tm-slot-pulse { animation: none; }
}
`;
